// web/src/tutorial/quickstart/string-flow.ts
// StringFlow · 快速上手（最短流程：选演奏者 → 选 MIDI → 生成动画）
//
// 写作原则（作者要求）：这一页只讲最短跑通流程，不展开任何参数。
// 参数细节全部下沉到《软件界面详细说明》/details/string-flow.html。
//
// 依据：H:\string_flow_rust
//   src/ui/app.rs（四个页签、顶栏）、avatar_panel.rs / avatar_display.rs / avatar_manager.rs、
//   midi_panel.rs、execution_panel.rs、fingering_panel.rs、data.rs（默认值）、
//   asset/i18n/zh-CN.json（界面原文）

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const stringFlowQuickstart: TutorialDoc = {
  key: "string_flow",
  slug: "string-flow",
  kind: "quickstart",
  icon: "🎻",
  title: l("String Flow 弓弦乐演奏动画", "String Flow Bowed-String Performance"),
  subtitle: l(
    "最短跑通流程：选一个演奏者、选一份 MIDI、点生成。全程不需要调任何参数，十来分钟拿到能直接在 Blender / Unreal 里加载的动画数据。",
    "The shortest path that works: pick a performer, pick a MIDI file, hit generate. Nothing to tune — in about ten minutes you have animation data your Blender or Unreal plugin can load.",
  ),
  readMinutes: 6,
  video: {
    url: "https://www.bilibili.com/video/BV15CSqBpEXp/",
    youtube: "https://www.youtube.com/watch?v=Bgu3Gi0UgRA",
    caption: l(
      "String Flow 完整操作演示：2.stringFlow使用演示（B 站）",
      "String Flow full walkthrough: 8 StringFlow使用演示和一些设置细节 (YouTube)",
    ),
    series: [
      { title: l("1.stringFlow介绍视频", "1.stringFlow介绍视频"), url: "https://www.bilibili.com/video/BV1jBSqBdEmc/" },
      { title: l("8.StringFlow使用演示和一些设置细节", "8 StringFlow使用演示和一些设置细节"), url: "https://www.bilibili.com/video/BV1CE9ABLExf/", youtube: "https://www.youtube.com/watch?v=Bgu3Gi0UgRA" },
      { title: l("StringFlow-大提琴演示", "StringFlow-大提琴演示"), url: "https://www.bilibili.com/video/BV182fPBCEoB/" },
      { title: l("StringFlowUnreal跑通", "StringFlowUnreal跑通"), url: "https://www.bilibili.com/video/BV16gZyBqEZ4/" },
    ],
  },
  images: [
    {
      src: "/assets/image/products/string-flow/CN/角色设置.png",
      srcEn: "/assets/image/products/string-flow/EN/Avatar.png",
      label: l("角色设置", "Avatar Setup"),
      width: 914,
      height: 882,
    },
    {
      src: "/assets/image/products/string-flow/CN/MIDI处理.png",
      srcEn: "/assets/image/products/string-flow/EN/MIDI.png",
      label: l("MIDI 处理", "MIDI"),
      width: 914,
      height: 882,
    },
    {
      src: "/assets/image/products/string-flow/CN/执行操作.png",
      srcEn: "/assets/image/products/string-flow/EN/Execute.png",
      label: l("执行操作", "Execute"),
      width: 914,
      height: 882,
    },
    {
      src: "/assets/image/products/string-flow/CN/指法检查.png",
      srcEn: "/assets/image/products/string-flow/EN/Fingering.png",
      label: l("指法检查", "Fingering"),
      width: 1920,
      height: 1040,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "overview",
      title: l("这一篇要做什么", "What you will do"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "String Flow 把一份 MIDI 变成**弓弦乐器**（小提琴 / 中提琴 / 大提琴 / 倍低音提琴）的演奏动画。它负责算左手按在哪根弦第几品、用哪根手指，右手的弓怎么走，以及四根琴弦怎么振，最后写出一份插件能直接加载的结果文件。整段动画没有一帧需要你手动摆。",
            "String Flow turns a MIDI file into **bowed-string** performance animation (violin, viola, cello or double bass). It works out which string and fret the left hand stops, which finger presses it, how the bow moves, and how the four strings vibrate — then writes a result file the plugins load directly. You never hand-key a frame.",
          ),
        },
        {
          kind: "p",
          text: l(
            "你要做的只有三件事，顺序也是固定的。",
            "There are only three things to do, always in this order.",
          ),
        },
        {
          kind: "steps",
          items: [
            {
              title: l("选演奏者", "Pick a performer"),
              text: l(
                "在 `⚙ 角色设置` 页的下拉菜单里选中这次要用的演奏者，其余保持默认。",
                "Choose the performer you want from the dropdown on the `⚙ 角色设置` / ⚙ Avatar Setup tab and leave everything else at its default.",
              ),
            },
            {
              title: l("选 MIDI", "Pick a MIDI file"),
              text: l(
                "在 `🎵 MIDI处理` 页选文件、扫描、勾上要生成动画的轨道。",
                "On the `🎵 MIDI处理` / 🎵 MIDI tab, choose the file, scan it, and tick the tracks you want animated.",
              ),
            },
            {
              title: l("生成动画", "Generate"),
              text: l(
                "在 `▶ 执行操作` 页点 `一键执行`，等控制台跑完，拿到 `output/final_result/` 下的 `.string_flow` 文件。",
                "On the `▶ 执行操作` / ▶ Execute tab click `一键执行` / Run All, wait for the console to finish, and collect the `.string_flow` file under `output/final_result/`.",
              ),
            },
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          title: l("先跑通，再调参", "Get it working first"),
          text: l(
            "这一页的所有数值都可以不动。等你看过一遍生成结果，再按需去调 `FPS`、`弓速`、`揉弦`、运弓策略这些参数。",
            "Every number on this page can stay as it is. Once you have seen a result, come back and adjust `FPS`, bow speed, vibrato or the bow strategy as needed.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "prepare",
      title: l("开始之前", "Before you start"),
      blocks: [
        {
          kind: "p",
          text: l(
            "只需要准备一份 MIDI 文件，`.mid` 或 `.midi` 都行。演奏者是软件自带的，从下拉菜单里选，不需要自己做模型、不需要调骨骼。",
            "All you need is a MIDI file — `.mid` or `.midi`. The performers ship with the app and are chosen from a dropdown; there is no model or rig work to do first.",
          ),
        },
        {
          kind: "p",
          text: l(
            "发行目录里通常已经带了好几个角色（小提琴、大提琴等），直接选就行。确实缺你想要的角色时，可以在 `⚙ 角色设置` 页用 `➕ 新增` 加一个：填名字、选一张预览图，再指向 Blender 插件侧导出的 `.violinist` 角色配置文件。第一次跑通可以先跳过这件事。",
            "A handful of performers (violin, cello and so on) usually ship with the release — just pick one. If the one you want is missing, add it on the `⚙ 角色设置` / ⚙ Avatar Setup tab with `➕ 新增` / ➕ Add: give it a name, pick a preview image, and point it at a `.violinist` performer profile exported from the Blender plugin side. You can skip this entirely the first time.",
          ),
        },
        {
          kind: "note",
          text: l(
            "程序只处理你在 `🎵 MIDI处理` 页勾选的轨道，所以多轨 MIDI 要先想好要哪几条。",
            "The app only processes the tracks you tick on the `🎵 MIDI处理` / 🎵 MIDI tab, so with a multi-track MIDI decide which ones you want up front.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "ui-overview",
      title: l("界面总览：四个页签怎么走", "The four tabs, in order"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "窗口顶部从左到右排着四个页签，按顺序点过去就是完整流程。主流程只用前三个，第四个是生成之后的核对页。",
            "Four tabs sit across the top of the window. Walking them left to right is the whole workflow — the first three are all you need, and the fourth is for checking the result.",
          ),
        },
        {
          kind: "ul",
          items: [
            l(
              "`⚙ 角色设置` —— 选演奏者，确认他的乐器和四根弦。",
              "`⚙ 角色设置` / ⚙ Avatar Setup — pick the performer and confirm their instrument and four strings.",
            ),
            l(
              "`🎵 MIDI处理` —— 选 MIDI 文件，扫描轨道，勾出要生成动画的那几条。",
              "`🎵 MIDI处理` / 🎵 MIDI — load the MIDI file, scan its tracks and tick the ones to animate.",
            ),
            l(
              "`▶ 执行操作` —— 点生成，然后在右边的控制台看进度。",
              "`▶ 执行操作` / ▶ Execute — hit generate and watch the console on the right.",
            ),
            l(
              "`🎼 指法检查` —— 把生成出来的左手按弦画成 TAB 谱面，用来核对；不生成任何文件，不看也不影响流程。",
              "`🎼 指法检查` / 🎼 Fingering — draws the generated left-hand fingering as a TAB score for checking. It writes nothing, so skipping it changes nothing.",
            ),
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "fine-tuning",
      title: l("要细调参数，看详细说明", "Tuning parameters? Read the reference"),
      blocks: [
        {
          kind: "p",
          text: l(
            "本文只讲最短流程。软件里每一个可调参数——`FPS`、`更高八度`、`弓速`、`揉弦速度`、`揉弦幅度`、运弓策略、`使用通道`、`音符量化`、轨道列表里的两列勾选、四个处理按钮，以及指法检查页的播放与缩放——都在[软件界面详细说明](/details/string-flow.html)里逐个讲清楚：它管什么、什么时候该动它、调大调小分别会变成什么样。",
            "This page covers the shortest path only. Every adjustable parameter in the app — `FPS`, the higher-octave checkbox, bow speed, vibrato speed and amplitude, the bow strategy, `使用通道` / Use Channel, `音符量化` / Note Quantization, both checkbox columns in the track list, the four processing buttons, and playback and zoom on the fingering tab — is documented one by one in the [UI reference](/details/string-flow.html): what it controls, when to touch it, and what changes when you do.",
          ),
        },
        {
          kind: "note",
          text: l(
            "跑通一次之后想改效果，直接去那一页按名字查；界面标签两边写法完全一致，用 Ctrl+F 搜按钮名最快。",
            "Once you have a working run and want to change the result, look the parameter up there. The labels match the UI exactly, so Ctrl+F on the button name is the fastest route.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "performer",
      title: l("第 1 步：选演奏者", "Step 1: pick the performer"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `⚙ 角色设置`，在左上角 `01 角色选择` 卡片里用下拉菜单选中演奏者。菜单里列的是已经保存的角色；如果这里显示的是 `未选择`，说明一个都还没加载到，先去 `02 角色信息` 用 `➕ 新增` 建一个。",
            "Open the `⚙ 角色设置` / ⚙ Avatar Setup tab and pick the performer from the dropdown in the `01 角色选择` / Select Avatar card at the top left. The menu lists the saved performers; if it reads `未选择` / Not selected, none are loaded yet — create one with `➕ 新增` / ➕ Add in the `02 角色信息` / Avatar Info card first.",
          ),
        },
        {
          kind: "p",
          text: l(
            "选好之后，下面会显示这个角色的信息：乐器（`小提琴` / `中提琴` / `大提琴` / `倍低音提琴`）、`4 根弦`、四根弦的空弦音高（`弦1: E5` 这样一枚一枚的徽章），以及当前的 `调弦方案:`。这些是回显，用来确认你选对了人——看到大提琴的字样却想生成小提琴动画，那就是选错了。",
            "Once selected, the card below shows who you picked: the instrument (`小提琴` / Violin / `中提琴` / Viola / `大提琴` / Cello / `倍低音提琴` / Double Bass), `4 根弦` / 4 strings, the open-string pitches as one badge per string (`弦1: E5` / String 1: E5 and so on), and the current `调弦方案:` / Tuning scheme. These are read-outs that confirm you picked the right performer — if it says cello and you wanted a violin, you picked the wrong one.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          title: l("下面两块可以完全不动", "The two cards below can stay untouched"),
          text: l(
            "`03 生成参数`（`FPS` 60、`弓速` 1.2、`揉弦速度` 1.0、`揉弦幅度` 1.0）和 `04 运弓策略`（默认 `长弓模式`）的默认值就是为「先跑通」准备的。想细调请去[软件界面详细说明](/details/string-flow.html)。",
            "The defaults in `03 生成参数` / Generation Parameters (`FPS` 60, bow speed 1.2, vibrato speed 1.0, vibrato amplitude 1.0) and `04 运弓策略` / Bow Strategy (default `长弓模式` / Long bow) are exactly what a first run needs. To tune them, see the [UI reference](/details/string-flow.html).",
          ),
        },
        {
          kind: "p",
          text: l(
            "右侧 `02 角色信息` 里的 `✏ 修改` / `➕ 新增` / `🗑 删除` 是管理角色列表用的，跑通流程不需要它们。",
            "The `✏ 修改` / ✏ Edit / `➕ 新增` / ➕ Add / `🗑 删除` / 🗑 Delete buttons in the `02 角色信息` / Avatar Info card manage the performer list; a first run never needs them.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "midi",
      title: l("第 2 步：选 MIDI、勾轨道", "Step 2: load the MIDI and tick its tracks"),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `🎵 MIDI处理`。左栏是文件与参数，右栏是扫描出来的轨道和四个处理按钮。",
            "Switch to the `🎵 MIDI处理` / 🎵 MIDI tab. Files and parameters are on the left; the scanned tracks and four processing buttons are on the right.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "点 `01 MIDI 文件` 里的 `选择 MIDI 文件` 挑文件；也可以直接把文件拖到下面那个 `📥 将 MIDI 文件拖放到此处` 的虚线框里。选好后卡片里会显示文件名和完整路径。",
              "Click `选择 MIDI 文件` / Select MIDI File in the `01 MIDI 文件` / MIDI File card, or drag the file straight onto the dashed `📥 将 MIDI 文件拖放到此处` / 📥 Drop a MIDI file here box below. The card then shows the file name and its full path.",
            ),
            l(
              "点 `扫描 MIDI 信息`。右边 `03 轨道列表` 会列出每一条有音符的轨道：轨道号、通道、乐器、音符数。",
              "Click `扫描 MIDI 信息` / Scan MIDI Info. The `03 轨道列表` / Track List table on the right lists every track that contains notes: track number, channel, instrument and note count.",
            ),
            l(
              "在 `选择` 一列勾上要做成动画的轨道。独奏曲通常只有一条主旋律轨；多轨合奏可以把几条都勾上，它们会一起参与运弓分配。",
              "Tick the tracks you want animated in the `选择` / Select column. A solo piece usually has one melodic track; for an ensemble, tick several — they all take part in the bowing assignment.",
            ),
            l(
              "点右下角 `04 处理操作` 里的 `添加选中的轨道`。左栏 `02 处理参数` 的 `已选择的轨道` 下会出现 `轨道 N` 徽章，表示已经加入。改了勾选就要再点一次这个按钮。",
              "Click `添加选中的轨道` / Add Selected Tracks in the `04 处理操作` / Actions card at the bottom right. A `轨道 N` / Track N badge appears under `已选择的轨道` / Selected Tracks in the left column's `02 处理参数` / Processing Parameters card — that means the tracks were committed. Change the ticks and you must click it again.",
            ),
          ],
        },
        {
          kind: "p",
          text: l(
            "这一页还有 `使用通道` 和 `音符量化` 两个参数，保持默认（`-1` 与 `1/16`）就好；右侧的 `弦乐分轨（四重奏）`、`修剪 MIDI 音符`、`速度均一化处理` 是可选预处理，第一次跑通也不用点。",
            "Two parameters live on this tab — `使用通道` / Use Channel and `音符量化` / Note Quantization — and both are fine at their defaults (`-1` and `1/16`). The `弦乐分轨（四重奏）` / Split String Quartet, `修剪 MIDI 音符` / Trim MIDI Notes and `速度均一化处理` / Normalize Velocity buttons on the right are optional preprocessing; skip them on a first run.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "execute",
      title: l("第 3 步：点生成", "Step 3: generate"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `▶ 执行操作`。左栏 `01 角色概览` 和 `02 MIDI 参数` 把前两步的选择原样回显一遍——「谁、拉哪首、哪几条轨道」，一眼扫过去确认没错。",
            "Switch to the `▶ 执行操作` / ▶ Execute tab. The `01 角色概览` / Avatar Overview and `02 MIDI 参数` / MIDI Parameters cards on the left echo the previous two steps: who is playing, which file, which tracks. One glance is enough.",
          ),
        },
        {
          kind: "p",
          text: l(
            "然后在左下角 `03 执行操作` 的 `快速执行` 里点 `一键执行`。右边的 `控制台输出` 开始滚动：初始化 → 生成左手指法 → 生成右手指法 → 生成动画，跑完最后一行是 `一键执行完成`。",
            "Then click `一键执行` / Run All under `快速执行` / Quick run in the `03 执行操作` / Operations card. The `控制台输出` / Console Output panel on the right starts scrolling: initialize → left-hand fingering → right-hand bowing → animation, ending with `一键执行完成` / All steps complete.",
          ),
        },
        {
          kind: "p",
          text: l(
            "想看清每一步，也可以用 `分步执行` 的两个按钮：先 `1. 生成指法`，再 `2. 生成动画`。第二步读的是第一步写出的文件，顺序不能反。",
            "To watch each stage, use the two buttons under `分步执行` / Step by step: `1. 生成指法` / 1. Generate Fingering first, then `2. 生成动画` / 2. Generate Animation. The second step reads what the first one wrote, so the order matters.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("跑的时候可以停", "You can stop a run"),
          text: l(
            "执行期间按钮旁会出现 `正在执行...` 和一个 `❌ 取消`。点它只停止当前任务，已经写出的文件留在 `output/` 里，可以重新跑。长曲子看到 `进度: 501/1670, 速度: 368.37 步/秒` 这样的行属正常。",
            "While a run is in progress you get a `正在执行...` / Running... label and a `❌ 取消` / ❌ Cancel button. Cancelling only stops the current task; whatever was already written stays in `output/` and you can run again. Long pieces legitimately show lines like `进度` / Progress: 501/1670, `速度` / speed: 368.37 steps/sec.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "result",
      title: l("生成出来的是什么、怎么用", "What you got, and where to use it"),
      image: { image: 3 },
      blocks: [
        {
          kind: "p",
          text: l(
            "真正要交给插件的是 `output/final_result/` 下的 **`.string_flow`** 文件。它是一份 JSON，里面记着左手动画、右手动画、琴弦动画和活动曲线的**绝对路径**——所以 `output/` 整个目录都别改名、别搬走、别只把 `.string_flow` 单独拷到别的机器上。",
            "The file your plugin needs is the **`.string_flow`** under `output/final_result/`. It is a JSON recording the **absolute paths** of the left-hand animation, right-hand animation, string animation and activity curve — so do not rename or move the `output/` folder, and do not copy the `.string_flow` on its own to another machine.",
          ),
        },
        {
          kind: "p",
          text: l(
            "用控制台右上角的 `复制结果` 可以一键把这份文件的绝对路径复制到剪贴板；顺手也把 `output/` 的位置记下来。",
            "Use `复制结果` / Copy Result at the top right of the console to copy that absolute path to the clipboard in one click, and note where `output/` lives while you are there.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "**Blender**：启用 MusicDoll 插件，在 3D 视图侧栏的 `MusicDoll` 面板下找到 `StringFlow` 子面板，在最下面的 `生成动画` 区里把路径框指到刚才那份 `.string_flow`，点 `一键生成全部动画`。",
              "**Blender**: enable the MusicDoll add-on, find the `StringFlow` sub-panel under the `MusicDoll` panel in the 3D view sidebar, point the path field in the `生成动画` / Generate Animation box at the `.string_flow` you just generated and click `一键生成全部动画`.",
            ),
            l(
              "**Unreal**：在对应的 MusicDoll Unreal 面板里用 `Animation File Path` 指到同一份文件，点 `Generate All Animation`。引擎侧的演奏者要用 `_unreal.violinist` 那一版角色配置。",
              "**Unreal**: point `Animation File Path` at the same file in the matching MusicDoll Unreal panel and click `Generate All Animation`. The engine-side performer needs the `_unreal.violinist` version of the profile.",
            ),
            l(
              "生成动画前，确认插件场景里的演奏者和程序里选中的是**同一个角色**，否则关键帧会落到错误的骨架上。",
              "Before generating, make sure the performer in the plugin scene is the **same one** you selected in the app, or the keyframes land on the wrong rig.",
            ),
          ],
        },
        {
          kind: "p",
          text: l(
            "进插件之前想先核对一遍，可以切到 `🎼 指法检查`：它把生成出来的左手按弦画成一张「弦 × 时间」的 TAB 谱面，点 `▶ 播放` 就会按 fps 走谱面、同时用 MIDI 输出把你选的那份原曲（含伴奏）弹出来，听到的和看到的一定一致。这一页只看不写，确认完直接去插件即可。",
            "If you want to check the result before opening the plugin, switch to `🎼 指法检查` / 🎼 Fingering: it draws the generated left-hand fingering as a string-by-time TAB score, and `▶ 播放` / ▶ Play advances the playhead while playing your MIDI file (accompaniment included) through the MIDI output — what you hear always matches what you see. That tab only reads, so you can close it and go straight to the plugin.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "checklist",
      title: l("跑通前的自检清单", "Quick checklist before you run"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "卡住的时候按这张清单从上往下核一遍，绝大多数问题都能定位到某一步没做完。",
            "If something is not working, walk this list top to bottom — almost every problem turns out to be one step left unfinished.",
          ),
        },
        {
          kind: "ul",
          items: [
            l(
              "MIDI 文件真的是 `.mid` 或 `.midi`，并且已经在 `🎵 MIDI处理` 的 `01 MIDI 文件` 里显示出来。",
              "The MIDI file really is `.mid` or `.midi` and already shows up in `01 MIDI 文件` / MIDI File.",
            ),
            l(
              "`⚙ 角色设置` 的 `01 角色选择` 里选中的是你要用的那个角色，不是 `未选择`。",
              "`01 角色选择` / Select Avatar holds the performer you want — not `未选择` / Not selected.",
            ),
            l(
              "`03 轨道列表` 里至少勾了一条轨道，并且点过 `添加选中的轨道`；`02 处理参数` 的 `已选轨道数` 不是 0。",
              "At least one row is ticked in `03 轨道列表` / Track List and you clicked `添加选中的轨道` / Add Selected Tracks; `已选轨道数` / Selected Track Count is not 0.",
            ),
            l(
              "`▶ 执行操作` 页 `02 MIDI 参数` 下的三枚徽章按 `初始化已完成` → `指法已生成` → `动画已生成` 依次亮起。",
              "The three badges under `02 MIDI 参数` / MIDI Parameters come on in order: `初始化已完成` / Initialization done, `指法已生成` / Fingering generated, `动画已生成` / Animation generated.",
            ),
            l(
              "控制台最后出现 `一键执行完成`（分步执行时是 `指法生成完成` 与 `弦动画已保存到: ...`）。",
              "The console ends with `一键执行完成` / All steps complete (or, for a step-by-step run, `指法生成完成` / Fingering generation complete and `弦动画已保存到` / String animation saved to: `...`).",
            ),
            l(
              "`output/final_result/` 下有一份以 `.string_flow` 结尾的文件。",
              "There is a file ending in `.string_flow` under `output/final_result/`.",
            ),
            l(
              "程序里选的演奏者和插件场景里的角色是同一个，而且 `output/` 目录没有被改名或搬动过。",
              "The performer selected in the app matches the one in the plugin scene, and `output/` has not been renamed or moved.",
            ),
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("常见问题", "Common questions"),
      blocks: [
        {
          kind: "callout",
          tone: "info",
          title: l("点了 `一键执行` 好像没反应？", "I clicked `一键执行` / Run All and nothing seems to happen"),
          text: l(
            "控制台会先把原因打出来：`错误: 未选择有效角色` 说明第 1 步没选人，`错误: 未选择MIDI文件` 说明第 2 步没选文件。回对应页补上再点一次。",
            "The console prints the reason first: `错误: 未选择有效角色` / Error: no valid avatar selected means step 1 has no performer, `错误: 未选择MIDI文件` / Error: no MIDI file selected means step 2 has no file. Fix that tab and click again.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("生成完了，插件里加载没反应？", "Generation finished but the plugin does not pick it up"),
          text: l(
            "确认加载的是 `output/final_result/` 里的 `.string_flow`（不是记录器 json），并且 `output/` 没有被改名或搬走——文件里存的是绝对路径。",
            "Check that you are loading the `.string_flow` from `output/final_result/` (not a recorder json) and that `output/` was not renamed or moved — the file stores absolute paths.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("一首曲子要跑多久？", "How long does one song take"),
          text: l(
            "取决于音符数量。控制台里的 `进度: x/y` 与 `速度: N 步/秒` 就是实时进度，大编制曲目跑几分钟很正常，跑的时候可以做别的事。",
            "It depends on the note count. The `进度` / Progress: x/y and `速度` / speed: N steps/sec lines are live progress; a few minutes is normal for a dense arrangement, so let it run.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("生成的动画和谱子对不上？", "The animation does not match the score"),
          text: l(
            "多数是轨道选错、多选或漏选。回 `🎵 MIDI处理` 重新勾选、再点一次 `添加选中的轨道`，然后回 `▶ 执行操作` 重跑。",
            "Usually the track selection is wrong, or a track was missed. Go back to `🎵 MIDI处理` / 🎵 MIDI, re-tick, click `添加选中的轨道` / Add Selected Tracks again, then re-run from `▶ 执行操作` / ▶ Execute.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("`🎼 指法检查` 页一定要看吗？", "Do I have to use the `🎼 指法检查` / 🎼 Fingering tab"),
          text: l(
            "不用。它是生成之后的核对工具，只看不写；不看也不会影响生成出来的文件。",
            "No. It is a read-only checking tool for the generated result; skipping it changes nothing about the output files.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("想换成大提琴 / 中提琴怎么办？", "How do I switch to cello or viola"),
          text: l(
            "在 `⚙ 角色设置` 里选一个乐器就是大提琴（或中提琴）的角色即可。乐器类型来自角色的 `.violinist` 配置文件，不是在参数里切的。",
            "Pick a performer whose instrument is the cello (or viola) on the `⚙ 角色设置` / ⚙ Avatar Setup tab. The instrument type comes from the performer's `.violinist` profile; there is no switch for it in the parameters.",
          ),
        },
      ],
    },
  ],
};
